import React from 'react'
import Helmet from 'react-helmet'

import type { OnTriggerPromoCode } from '../../cta'
import { getCmsErrorPageStatus, PageRenderer } from '../../page'
import type { CmsErrorPageStatus } from '../../page'
import { useCmsBlock } from '../../store'
import type { CmsFetcher } from '../../store'

import { getCmsSeo } from './seo'

const CMS_PAGE_CONTENT_TYPE = 'cms_page'

type Props = {
  pageUrl: string
  cmsFetcher: CmsFetcher
  onTriggerPromocode: OnTriggerPromoCode
  renderError?: (status: CmsErrorPageStatus) => React.ReactNode
}

// Resolves and renders any CMS page from its `pageUrl` — no per-page code.
export function DynamicCmsPage({ pageUrl, cmsFetcher, onTriggerPromocode, renderError }: Props) {
  const { data, loading, error, errorCode } = useCmsBlock(
    { contentType: CMS_PAGE_CONTENT_TYPE, pageUrl },
    cmsFetcher,
  )

  if (loading) {
    return null
  }

  if (error) {
    return renderError ? <>{renderError(getCmsErrorPageStatus(errorCode))}</> : null
  }

  // Pristine, not-yet-fetched state — render nothing until the first fetch resolves, not the 404 page.
  if (!data) {
    return null
  }

  const seo = getCmsSeo(data, pageUrl)

  return (
    <div data-testid="dynamic-cms-page">
      <Helmet title={seo.title} link={[{ rel: 'canonical', href: seo.canonical }]}>
        <meta name="description" content={seo.description} />
        <meta name="robots" content={seo.robots} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.canonical} />
        <meta property="og:image" content={seo.ogImage} />
      </Helmet>
      <PageRenderer page={data} onTriggerPromocode={onTriggerPromocode} />
    </div>
  )
}
